import { useState, useEffect } from 'react';
import { Loader2, Mail, Pencil, Plus, Trash2, Save, Download, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import * as api from '@/services/apiClient';
import { toast } from 'sonner';

const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

/**
 * Shows the invoice as the customer will receive it, before it is sent.
 *
 * Emailing was previously a single click with nothing shown first, so a wrong
 * figure or a wrong address was only discovered once the invoice had already
 * left — and an issued invoice cannot be taken back, only cancelled.
 *
 * Editing is offered here rather than on a separate screen because this is the
 * moment someone actually reads the numbers.
 */
export default function InvoicePreviewDialog({ invoice, open, onClose, onSent, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [lines, setLines] = useState([]);
    const [notes, setNotes] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!invoice) return;
        setEditing(false);
        setLines(invoice.lineItems?.map(li => ({ ...li })) || []);
        setNotes(invoice.notes || '');
        setDueDate(invoice.dueDate ? new Date(invoice.dueDate).toISOString().slice(0, 10) : '');
    }, [invoice, open]);

    if (!invoice) return null;

    // Once it is sent or part paid, amending in place would misrepresent what
    // the customer already holds.
    const locked = !!invoice.emailedAt || invoice.amountPaid > 0
        || ['cancelled', 'void'].includes(invoice.status);

    const lockReason = invoice.emailedAt
        ? `Already sent to ${invoice.emailedTo?.join(', ')} — cancel and raise a new invoice to change it.`
        : invoice.amountPaid > 0
            ? 'Payments are recorded against this invoice — cancel and raise a new one to change it.'
            : 'This invoice has been cancelled.';

    const setLine = (i, field, value) =>
        setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

    const addLine = () =>
        setLines(ls => [...ls, { type: 'custom', description: '', quantity: 1, unitPrice: 0, amount: 0 }]);

    const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

    const save = async () => {
        setBusy(true);
        try {
            const res = await api.updateInvoice(invoice._id, {
                lineItems: lines.map(l => ({
                    type: l.type,
                    description: l.description,
                    quantity: Number(l.quantity) || 1,
                    unitPrice: Number(l.unitPrice) || 0,
                    amount: Number(l.amount) || 0,
                    sacCode: l.sacCode || '',
                })),
                notes,
                dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
            });
            toast.success(res.message);
            setEditing(false);
            onUpdated?.(res.data);
        } catch (err) {
            toast.error(err.message || 'Could not update the invoice.');
        } finally {
            setBusy(false);
        }
    };

    const send = async () => {
        setBusy(true);
        try {
            const res = await api.emailInvoice(invoice._id);
            toast.success(res.message);
            onSent?.();
        } catch (err) {
            toast.error(err.message || 'Could not send the invoice.');
        } finally {
            setBusy(false);
        }
    };

    const to = invoice.billTo?.billingEmail;
    const tax = invoice.tax || {};

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{invoice.invoiceNumber}</DialogTitle>
                    <DialogDescription>
                        {editing
                            ? 'Amending before it is sent. Nothing has left yet.'
                            : to
                                ? <>This is what will be emailed to <strong className="text-foreground">{to}</strong>.</>
                                : 'No billing email on this invoice.'}
                    </DialogDescription>
                </DialogHeader>

                {locked && (
                    <p className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{lockReason}</span>
                    </p>
                )}

                {/* Parties */}
                <div className="grid gap-4 border-b border-border pb-4 text-sm sm:grid-cols-2">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">From</p>
                        <p className="mt-1 font-medium">{invoice.billFrom?.legalName}</p>
                        <p className="text-xs text-muted-foreground">
                            {[invoice.billFrom?.city, invoice.billFrom?.state].filter(Boolean).join(', ')}
                        </p>
                        {invoice.billFrom?.gstin && (
                            <p className="font-mono text-[11px] text-muted-foreground">GSTIN {invoice.billFrom.gstin}</p>
                        )}
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">To</p>
                        <p className="mt-1 font-medium">{invoice.billTo?.legalName || invoice.billTo?.companyName}</p>
                        <p className="text-xs text-muted-foreground">
                            {[invoice.billTo?.city, invoice.billTo?.state].filter(Boolean).join(', ')}
                        </p>
                        {invoice.billTo?.gstin && (
                            <p className="font-mono text-[11px] text-muted-foreground">GSTIN {invoice.billTo.gstin}</p>
                        )}
                    </div>
                </div>

                {/* Lines */}
                <div className="space-y-2">
                    {!editing && (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left">
                                    <th className="pb-2 font-medium text-muted-foreground">Description</th>
                                    <th className="pb-2 text-right font-medium text-muted-foreground">Qty</th>
                                    <th className="pb-2 text-right font-medium text-muted-foreground">Rate</th>
                                    <th className="pb-2 text-right font-medium text-muted-foreground">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {invoice.lineItems?.map((li, i) => (
                                    <tr key={i}>
                                        <td className="py-2">{li.description}</td>
                                        <td className="py-2 text-right tabular-nums">{li.quantity}</td>
                                        <td className="py-2 text-right tabular-nums">{money(li.unitPrice)}</td>
                                        <td className="py-2 text-right tabular-nums">
                                            {li.type === 'discount' ? `-${money(li.amount)}` : money(li.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {editing && (
                        <div className="space-y-2">
                            {lines.map((l, i) => (
                                <div key={i} className="grid grid-cols-12 items-center gap-2">
                                    <Input
                                        className="col-span-5 h-8 text-sm" placeholder="Description"
                                        value={l.description} onChange={e => setLine(i, 'description', e.target.value)}
                                    />
                                    <select
                                        className="col-span-2 h-8 rounded-md border border-input bg-background px-2 text-xs"
                                        value={l.type} onChange={e => setLine(i, 'type', e.target.value)}
                                    >
                                        <option value="plan">Plan</option>
                                        <option value="addon">Add-on</option>
                                        <option value="custom">Custom</option>
                                        <option value="discount">Discount</option>
                                    </select>
                                    <Input
                                        className="col-span-1 h-8 text-sm" type="number" min="0"
                                        value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)}
                                    />
                                    <Input
                                        className="col-span-3 h-8 text-sm" type="number" step="0.01" placeholder="Amount"
                                        value={l.amount}
                                        onChange={e => { setLine(i, 'amount', e.target.value); setLine(i, 'unitPrice', e.target.value); }}
                                    />
                                    <button
                                        type="button" onClick={() => removeLine(i)}
                                        className="col-span-1 text-muted-foreground hover:text-destructive"
                                        aria-label="Remove line"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={addLine}>
                                <Plus className="h-3 w-3" /> Add line
                            </Button>

                            <div className="grid gap-3 pt-2 sm:grid-cols-2">
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground">Due date</label>
                                    <Input type="date" className="mt-1 h-8 text-sm" value={dueDate}
                                        onChange={e => setDueDate(e.target.value)} />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-muted-foreground">Notes on the invoice</label>
                                    <Input className="mt-1 h-8 text-sm" value={notes}
                                        onChange={e => setNotes(e.target.value)} />
                                </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                                Totals and tax recalculate on save, using the place of supply already
                                fixed on this invoice.
                            </p>
                        </div>
                    )}
                </div>

                {/* Totals */}
                {!editing && (
                    <dl className="ml-auto w-full max-w-xs space-y-1 border-t border-border pt-3 text-sm">
                        <div className="flex justify-between">
                            <dt className="text-muted-foreground">Taxable value</dt>
                            <dd className="tabular-nums">{money(invoice.taxableValue)}</dd>
                        </div>
                        {tax.mode === 'cgst_sgst' && (
                            <>
                                <div className="flex justify-between">
                                    <dt className="text-muted-foreground">CGST @ {tax.rate / 2}%</dt>
                                    <dd className="tabular-nums">{money(tax.cgst)}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-muted-foreground">SGST @ {tax.rate / 2}%</dt>
                                    <dd className="tabular-nums">{money(tax.sgst)}</dd>
                                </div>
                            </>
                        )}
                        {tax.mode === 'igst' && (
                            <div className="flex justify-between">
                                <dt className="text-muted-foreground">IGST @ {tax.rate}%</dt>
                                <dd className="tabular-nums">{money(tax.igst)}</dd>
                            </div>
                        )}
                        <div className="flex justify-between border-t border-border pt-1 font-semibold">
                            <dt>Total</dt>
                            <dd className="tabular-nums">{money(invoice.total)}</dd>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                            <dt>Due</dt>
                            <dd>{fmtDate(invoice.dueDate)}</dd>
                        </div>
                    </dl>
                )}

                <DialogFooter className="flex-wrap gap-2 border-t border-border pt-4">
                    <Button
                        size="sm" variant="outline" className="gap-1.5"
                        onClick={() => api.downloadInvoicePdf(invoice._id, invoice.invoiceNumber)
                            .catch(e => toast.error(e.message))}
                    >
                        <Download className="h-3.5 w-3.5" /> PDF
                    </Button>

                    {!locked && !editing && (
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditing(true)}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                    )}
                    {editing && (
                        <>
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
                                Cancel
                            </Button>
                            <Button size="sm" disabled={busy} className="gap-1.5" onClick={save}>
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                Save changes
                            </Button>
                        </>
                    )}
                    {!editing && !invoice.emailedAt && (
                        <Button size="sm" disabled={busy || !to} className="gap-1.5" onClick={send}>
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                            Send to {to || 'nobody'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
